import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const GROUP_MANUAL_MEMBERSHIP_SEARCH_RESOURCE = "group-manual-membership" as const;
export const GROUP_MANUAL_MEMBERSHIP_SEARCH_OPERATION = "search" as const;
export const GROUP_MANUAL_MEMBERSHIP_SEARCH_API_PATH =
  `/v1/${GROUP_MANUAL_MEMBERSHIP_SEARCH_RESOURCE}/${GROUP_MANUAL_MEMBERSHIP_SEARCH_OPERATION}` as const;

export type GroupManualMembershipSearchParameters =
  paths[typeof GROUP_MANUAL_MEMBERSHIP_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type GroupManualMembershipSearchBody =
  paths[typeof GROUP_MANUAL_MEMBERSHIP_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type GroupManualMembershipSearchResponse =
  paths[typeof GROUP_MANUAL_MEMBERSHIP_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useGroupManualMembershipSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<GroupManualMembershipSearchParameters, GroupManualMembershipSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    GroupManualMembershipSearchResponse,
    Error,
    GroupManualMembershipSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<
      PaginatedOptions<GroupManualMembershipSearchParameters, GroupManualMembershipSearchBody>
    >(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      GROUP_MANUAL_MEMBERSHIP_SEARCH_RESOURCE,
      GROUP_MANUAL_MEMBERSHIP_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<GroupManualMembershipSearchResponse>(
        GROUP_MANUAL_MEMBERSHIP_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
